import { AchIcon } from './icons';
import type { SeasonalState } from '../../api/types';

/** Тир медальона (L3): цвет «стареет» с уровнем; сезонные — зелёные/золотые.
 *  brass/brass-max — разовые достижения отбора (латунь), gold-max — залитое
 *  золото макс-ступени, legendary — M05 (design_handoff_mastery). */
export type Tier =
  | 'none'
  | 'graphite'
  | 'steel'
  | 'gold'
  | 'season'
  | 'brass'
  | 'brass-max'
  | 'gold-max'
  | 'legendary';

export function tierOf(level: number, state: SeasonalState | null): Tier {
  if (state === 'active' && level > 0) return 'season';
  if (state === 'fixed') return 'gold';
  if (level <= 0) return 'none';
  if (level >= 7) return 'gold';
  if (level >= 4) return 'steel';
  return 'graphite';
}

/**
 * Спец-акценты по коду определения (design_handoff_qualification 2 /
 * mastery): разовые sel_* — латунь (sel_champ — залитая), ступенчатые sel_* —
 * тиры по ступени 1..4, M05 — легендарный. null = обычный tierOf.
 */
export function accentOf(
  code: string | null | undefined,
  level: number,
  maxLevel: number,
): Tier | null {
  if (!code) return null;
  if (code === 'M05') return level >= 1 ? 'legendary' : null;
  if (!code.startsWith('sel_')) return null;
  if (maxLevel <= 1) {
    if (level < 1) return null;
    return code === 'sel_champ' ? 'brass-max' : 'brass';
  }
  if (level <= 0) return null;
  if (level >= maxLevel) return 'gold-max';
  return level === 1 ? 'graphite' : level === 2 ? 'steel' : 'gold';
}

type MedallionProps = {
  icon: string | null;
  level: number;
  maxLevel: number;
  /** 0..1 к следующему уровню; null/undefined — без кольца. */
  progress?: number | null;
  size?: number;
  state?: SeasonalState | null;
  /** Секретное неполученное: пунктирный круг + «?». */
  masked?: boolean;
  /** Показывать кольцо прогресса (в «Недавних» — нет). */
  ring?: boolean;
  /** Бейдж-кружок с номером уровня внизу-справа. */
  badge?: boolean;
  /** Золотое свечение выполненных MAX (витрина PF1). */
  glow?: boolean;
  /** Явный тир вместо tierOf (акценты отбора/легендарные — accentOf). */
  variant?: Tier | null;
};

/**
 * Медальон достижения с кольцом прогресса (L3). Кольцо — SVG-circle со
 * старта сверху; у выполненных (MAX) кольцо полное. Тир задаёт CSS-класс,
 * иконка наследует цвет через currentColor.
 */
export function Medallion({
  icon,
  level,
  maxLevel,
  progress,
  size = 46,
  state = null,
  masked = false,
  ring = true,
  badge = true,
  glow = false,
  variant = null,
}: MedallionProps) {
  const tier = variant ?? tierOf(level, state);
  const done = maxLevel > 0 && level >= maxLevel;
  const pct = done ? 1 : Math.max(0, Math.min(1, progress ?? 0));
  const showRing = ring && !masked && (level > 0 || pct > 0);
  const inner = Math.round(size * (showRing ? 0.76 : 0.9));
  const r = (size - 3) / 2;
  const dash = 2 * Math.PI * r;

  const cls = [
    'ach-medallion',
    `ach-medallion--${tier}`,
    masked ? 'ach-medallion--secret' : '',
    state === 'missed' ? 'ach-medallion--missed' : '',
    glow && done ? 'ach-medallion--glow' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={cls} style={{ width: size, height: size }}>
      {showRing && (
        <svg className="ach-ring" viewBox={`0 0 ${size} ${size}`} aria-hidden>
          <circle className="track" cx={size / 2} cy={size / 2} r={r} strokeWidth={3} />
          <circle
            className="fill"
            cx={size / 2}
            cy={size / 2}
            r={r}
            strokeWidth={3}
            strokeDasharray={dash}
            strokeDashoffset={dash * (1 - pct)}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        </svg>
      )}
      <span
        className="ach-medallion-core"
        style={{ width: inner, height: inner, fontSize: Math.round(inner * 0.45) }}
      >
        {masked ? '?' : <AchIcon id={icon} />}
      </span>
      {badge && !masked && level > 0 && <span className="ach-medallion-badge">{level}</span>}
    </span>
  );
}
